import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, InputGroup } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function IssuerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const location = useLocation();

    const issuerData = location.state?.issuer || null;
    const isEdit = Boolean(issuerData);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        payment_type: "",
        payment_value: "",
        settlement_type: "",
        opening_balance: "",
        status: "1", // Default to Active (1)
        password: "",
        password_confirmation: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [alert, setAlert] = useState(null);

    // States for password visibility toggle
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    });

    useEffect(() => {
        if (isEdit && issuerData) {
            setFormData({
                name: issuerData.name || "",
                email: issuerData.email || "",
                nickname: issuerData.nickname || "",
                payment_type: issuerData.payment_type || "",
                payment_value: issuerData.payment_value ?? "",
                settlement_type: issuerData.settlement_type || "",
                opening_balance: issuerData.opening_balance ?? "",
                status: issuerData.status !== undefined ? String(issuerData.status) : "1",
                password: "",
                password_confirmation: "",
            });
        }
    }, [isEdit, issuerData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const url = isEdit
            ? `${process.env.REACT_APP_API_URL}/admin/issuer/${issuerData.id}`
            : `${process.env.REACT_APP_API_URL}/admin/issuer`;

        try {
            const response = await axios.post(url, formData, getAuthHeader());

            if (response.data?.success) {
                navigate("/issuers");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {isEdit ? t("Edit Issuer") : t("Add Issuer")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/issuers" className="mc-breadcrumb-link">
                                            {t("Issuers")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {isEdit ? t("Edit") : t("Add")}
                                    </li>
                                </ul>
                            </div>
                            <Button variant="secondary" onClick={() => navigate("/issuers")}>
                                {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                {/* Expanded to full width xl={12} and removed top Card.Header block */}
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Name")} *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        isInvalid={!!errors.name}
                                        placeholder="Enter full name"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Nickname")} *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="nickname"
                                        value={formData.nickname}
                                        onChange={handleChange}
                                        isInvalid={!!errors.nickname}
                                        placeholder="Enter nickname"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.nickname?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Email")} *</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        isInvalid={!!errors.email}
                                        placeholder="Enter email address"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t("Payment Type")}</Form.Label>
                                            <Form.Select
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_type}
                                            >
                                                <option value="">{t("Select Payment Type")}</option>
                                                <option value="salary">{t("Salary")}</option>
                                                <option value="per_product">{t("Per Product")}</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.payment_type?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t("Payment Value")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="payment_value"
                                                value={formData.payment_value}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_value}
                                                placeholder="Enter payment value"
                                                max={formData.payment_type === "percentage" ? 100 : undefined}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.payment_value?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t("Settlement Type")}</Form.Label>
                                            <Form.Select
                                                name="settlement_type"
                                                value={formData.settlement_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.settlement_type}
                                            >
                                                <option value="">{t("Select Settlement Type")}</option>
                                                <option value="receivable">{t("Receivable")}</option>
                                                <option value="payable">{t("Payable")}</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.settlement_type?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t("Opening Balance")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="opening_balance"
                                                value={formData.opening_balance}
                                                onChange={handleChange}
                                                isInvalid={!!errors.opening_balance}
                                                placeholder="Enter opening balance"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.opening_balance?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Status")} *</Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        isInvalid={!!errors.status}
                                        required
                                    >
                                        <option value="1">{t("Active")}</option>
                                        <option value="0">{t("Inactive")}</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.status?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {isEdit ? t("New Password (Optional)") : t("Password *")}
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            isInvalid={!!errors.password}
                                            placeholder={
                                                isEdit
                                                    ? t("Leave empty if you don't want to change")
                                                    : t("Minimum 8 characters")
                                            }
                                            required={!isEdit}
                                        />

                                        <Button
                                            variant="primary"
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? "Hide" : "Show"}
                                        </Button>

                                        <Form.Control.Feedback type="invalid">
                                            {errors.password?.[0]}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>

                                {(formData.password || !isEdit) && (
                                    <Form.Group className="mb-4">
                                        <Form.Label>{t("Confirm Password")} *</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="password_confirmation"
                                                value={formData.password_confirmation}
                                                onChange={handleChange}
                                                placeholder={t("Confirm password")}
                                                required={!isEdit || Boolean(formData.password)}
                                            />
                                            <Button
                                                variant="primary"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                type="button"
                                            >
                                                {showConfirmPassword ? "Hide" : "Show"}
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>
                                )}

                                <div className="d-flex gap-2 justify-content-end">
                                    <Button
                                        variant="secondary"
                                        onClick={() => navigate("/issuers")}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting
                                            ? isEdit
                                                ? t("Updating...")
                                                : t("Saving...")
                                            : isEdit
                                                ? t("Update Issuer")
                                                : t("Save Issuer")}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}   